'use client';

import { BriefcaseIcon, MapPinIcon, TagIcon } from '@heroicons/react/16/solid';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/shift-away-subtle.css';
import React, { useContext } from 'react';
import classNames from 'classnames';
import parse from 'html-react-parser';
import { GateStyleContext } from './GateStyleContext';
import SafeTooltip from './SafeTooltip';
import { resolveSetting } from '../../utils/settingsHelper';
import { toSafeFontName } from '../../utils/fonts';
import { formatDisplayFieldValue } from '../selectors/Gate/JobCountHelpers';
import PublicProfile from './PublicProfile';
import ApplyButton from './ApplyButton';
import SalaryDetail from './SalaryDetail';
import { getFontSizeClasses } from '../selectors/Text/Text';
import ApplyStyleWrapper from './ApplyStyleWrapper';
import Video from '../selectors/Video/Video';
import { getDivStyle } from '../selectors/Basic/divHelpers';
import { roundedOptions, shadowOptions } from '../editor/Toolbar/options';

export default function RenderDetail({ job, children, showButton = true }) {
	const {
		settings, active, text, register, twoButtons, iconColor, showProfile, profileBelow, titleFontSize, titleColor,
		headingColor, headingFontSize, paragraphColor, fontSize, iconBefore, dark, documents,
		badgeBackground, badgeBorderColor, badgeRounded, badgeShadow, contentTextAlign, displayFields, fieldsMetaMap, pageLanguage,
		'heading.color': hColor,
		'heading.background': hBackground,
		'heading.background2': hBackground2,
		'heading.background3': hBackground3,
		'heading.bgGradientDirection': hBgGradientDirection,
		'heading.borderColor': hBorderColor,
		'heading.rounded': hRounded,
		'heading.shadow': hShadow,
		'heading.borderWidth': hBorderWidth,
		'heading.padding': hPadding,
		...button
	} = useContext(GateStyleContext);

	const headingDivProps = {
		color: hColor,
		background: hBackground,
		background2: hBackground2,
		background3: hBackground3,
		bgGradientDirection: hBgGradientDirection,
		borderColor: hBorderColor,
		rounded: hRounded,
		shadow: hShadow,
		borderWidth: hBorderWidth,
		padding: hPadding,
	};

	const { display: _d, alignItems: _a, flexDirection: _f, ...headingStyle } = getDivStyle(headingDivProps, settings);

	const resolvedBadgeBg = resolveSetting(badgeBackground, settings);
	const resolvedBadgeBorderColor = resolveSetting(badgeBorderColor, settings);
	const hasBadge = resolvedBadgeBg || resolvedBadgeBorderColor;
	const badgeClass = hasBadge ? classNames('px-2.5 py-0.5', roundedOptions[badgeRounded], shadowOptions[badgeShadow]) : '';
	const badgeStyle = {
		...(resolvedBadgeBg ? { background: resolvedBadgeBg } : {}),
		...(resolvedBadgeBorderColor ? { borderColor: resolvedBadgeBorderColor, borderWidth: 2, borderStyle: 'solid' } : {}),
	};
	const resolvedRounded = resolveSetting(hRounded, settings);
	const headingClasses = classNames(
		{ [roundedOptions[resolvedRounded]]: !!resolvedRounded },
		shadowOptions[hShadow],
	);

	const renderButton = (last = false) => (
		<ApplyButton
			active={active}
			{...button}
			settings={settings}
			text={text}
			jobLanguage={job.language}
			iconBefore={iconBefore}
			documents={documents}
			options={active ? { jobId: job.id } : null}
			initialOpen={last && register && active}
		/>
	);

	return (
		<div className={classNames('space-y-4 prose markers-color-inherit max-w-2xl prose-headings:mt-6 prose-headings:mb-3 prose-ul:mb-0 prose-p:my-0', { 'prose-invert': dark })}>
			<div className="@sm:flex items-baseline gap-4 space-y-2 @sm:space-y-0 @sm:justify-between">
				<div className="min-w-0 flex-1">
					<h1
						className={classNames('font-bold @sm:tracking-tight', getFontSizeClasses(resolveSetting(titleFontSize || 'headingFontSize', settings) || 24))}
						style={{ fontFamily: toSafeFontName(settings.headingFontFamily) || undefined, color: resolveSetting(titleColor, settings) }}
					>
						{job.title}
					</h1>
					<div className="mt-1 flex flex-col @sm:mt-0 @sm:flex-row @sm:flex-wrap">
						{job?.employmentForms?.length > 0 && (
							<div className={classNames('mt-2 flex items-center text-sm @sm:mr-2', badgeClass)} style={badgeStyle}>
								<BriefcaseIcon
									style={{ color: resolveSetting(iconColor, settings) }}
									aria-hidden="true"
									className="mr-1.5 h-5 w-5 flex-shrink-0"
								/>
								<span style={{ color: resolveSetting(paragraphColor, settings) }}>
									{job.employmentForms.map((i) => (i.locale ? i.locale[job.language] : i.name)).filter((i) => i).join(', ')}
								</span>
							</div>
						)}
						{job.location && (
							<div className={classNames('mt-2 flex items-center text-sm @sm:mr-2', badgeClass)} style={badgeStyle}>
								<MapPinIcon
									style={{ color: resolveSetting(iconColor, settings) }}
									aria-hidden="true"
									className="mr-1.5 h-5 w-5 flex-shrink-0"
								/>
								<span style={{ color: resolveSetting(paragraphColor, settings) }}>
									{job.location}
								</span>
							</div>
						)}
						<SalaryDetail
							info={job.salaryInfo}
							salary={job.salary}
							jobTitle={job.title}
							iconColor={resolveSetting(iconColor, settings)}
							color={resolveSetting(paragraphColor, settings)}
							badgeClass={badgeClass}
							badgeStyle={badgeStyle}
						/>
						{displayFields && displayFields.map((fieldName) => {
							const meta = fieldsMetaMap?.[fieldName];
							const previewValue = meta?.type === 'date' ? new Date() : meta?.options?.[0];
							const text = active
								? formatDisplayFieldValue(job.fields?.[fieldName], meta?.type, pageLanguage)
								: formatDisplayFieldValue(previewValue, meta?.type, pageLanguage);
							if (!text) return null;
							return (
								<SafeTooltip key={fieldName} placement="top" content={meta?.label || fieldName}>
									<div className={classNames('mt-2 flex items-center text-sm @sm:mr-2', badgeClass)} style={badgeStyle}>
										<TagIcon
											style={{ color: resolveSetting(iconColor, settings) }}
											aria-hidden="true"
											className="mr-1.5 h-5 w-5 flex-shrink-0"
										/>
										<span style={{ color: resolveSetting(paragraphColor, settings) }}>
											{text}
										</span>
									</div>
								</SafeTooltip>
							);
						})}
					</div>
				</div>
				{twoButtons && renderButton()}
			</div>
			{job.videoUrl && <Video url={job.videoUrl} />}
			{!job.videoUrl && job.file && (
				<img src={job.file.url} alt={job.file.name} className="w-full h-auto rounded-2xl" />
			)}
			<div>
				<ApplyStyleWrapper styles={
					{
						default: { className: getFontSizeClasses(fontSize), style: { color: resolveSetting(paragraphColor, settings), textAlign: contentTextAlign || undefined } },
						h2: {
							className: classNames(getFontSizeClasses(headingFontSize), headingClasses),
							style: {
								...headingStyle,
								color: headingStyle.color || resolveSetting(headingColor, settings),
								borderStyle: hBorderWidth ? 'solid' : undefined,
							},
						},
					}
				}
				>
					{parse(job.content)}
					{/* <JobContent>{job.content}</JobContent> */}
				</ApplyStyleWrapper>
			</div>
			{showButton && renderButton(true)}
			{children}
			{job.contactPerson && showProfile && (
				<PublicProfile user={job.contactPerson} profileBelow={profileBelow} />
			)}
		</div>
	);
}
